import { FIELD_DESCRIPTIONS } from '../field-descriptions';
import {
  COMPONENT_LABELS,
  formatNumberList,
  NEW_OUT_OF_BOOK,
  NEW_VOLATILITY,
  PROGRAM_TYPE_LABELS,
  parseNumberList,
  patchRules,
  type RulesInput,
  setProgramType,
  setRuleField,
  setRuleLive,
  toIsoDate,
} from '../mm-program-editing';
import { isPerpetual, PROGRAM_TYPES, type ProgramInput, type ProgramType, type RuleComponent } from '../mm-program-schema';

/*
 * The editable columns of the all-programs grid, in the order of the ops sheet (PID … NOTIONAL_COMP_PCT) followed by the
 * rules components, each mapped onto a program's contract fields. A column only applies where the contract allows the
 * field: per-bucket pay on independent buckets, program pay on least performing bucket, open volume and rules on perpetuals.
 */

export type FieldValue = number | boolean | string | undefined;
export type FieldKind = 'number' | 'boolean' | 'day' | 'text' | 'type';
export type FieldGroup = 'Schedule' | 'B1' | 'B2' | 'Index' | 'Pay' | 'Open volume' | 'Rest' | 'Linger' | 'Skew' | 'Volatility' | 'Out of book';

export interface GridField {
  key: string;
  label: string;
  /** Label with its group, for the bulk editor and change lists */
  longLabel: string;
  group: FieldGroup;
  kind: FieldKind;
  unit?: string;
  /** 100 to show a fraction as a percentage */
  scale?: number;
  description?: string;
  /** Only bucket widths and sizes may differ between the Core, Extended and Weekend schedules of a set */
  perSession: boolean;
  /** The contract path below the program, matching `findSettingsProblems` paths */
  path: string;
  appliesTo: (program: ProgramInput, instrument: string) => boolean;
  get: (program: ProgramInput) => FieldValue;
  set: (program: ProgramInput, value: FieldValue) => ProgramInput;
}

type BucketName = 'b1' | 'b2';
type IndependentProgram = Extract<ProgramInput, { type: 'independent_buckets' }>;
type LeastPerformingProgram = Extract<ProgramInput, { type: 'least_performing_bucket' }>;

const always = () => true;
const isIndependent = (program: ProgramInput): program is IndependentProgram => program.type === 'independent_buckets';
const isLeastPerforming = (program: ProgramInput): program is LeastPerformingProgram => program.type === 'least_performing_bucket';

/** Sets or, for undefined, removes a key, keeping the program's variant type. */
const withKey = <T extends object>(value: T, key: string, next: unknown): T => {
  const copy = { ...value } as Record<string, unknown>;
  if (next === undefined) delete copy[key];
  else copy[key] = next;
  return copy as T;
};

const bucketField = (bucket: BucketName, field: 'bps' | 'kpi' | 'nbbo', overrides: Pick<GridField, 'label' | 'kind' | 'unit'>): GridField => ({
  key: `${bucket}.${field}`,
  longLabel: `${bucket.toUpperCase()} ${overrides.label}`,
  group: bucket === 'b1' ? 'B1' : 'B2',
  description: FIELD_DESCRIPTIONS[field],
  perSession: field !== 'nbbo',
  path: `${bucket}.${field}`,
  appliesTo: always,
  get: (program) => (field === 'nbbo' ? program[bucket].nbbo === true : program[bucket][field]),
  set: (program, value) => ({ ...program, [bucket]: { ...program[bucket], [field]: field === 'nbbo' ? value === true : value } }) as ProgramInput,
  ...overrides,
});

/** Pay terms of one bucket; independent buckets only. Blank exponents are null there, as the contract requires the key. */
const bucketPayField = (bucket: BucketName, field: 'threshold' | 'compensation' | 'exponent_start' | 'exponent', overrides: Pick<GridField, 'label' | 'unit'>): GridField => ({
  key: `${bucket}.${field}`,
  longLabel: `${bucket.toUpperCase()} ${overrides.label.toLowerCase()}`,
  group: bucket === 'b1' ? 'B1' : 'B2',
  kind: 'number',
  description: FIELD_DESCRIPTIONS[field],
  perSession: false,
  path: `${bucket}.${field}`,
  appliesTo: isIndependent,
  get: (program) => (isIndependent(program) ? (program[bucket][field] ?? undefined) : undefined),
  set: (program, value) => {
    if (!isIndependent(program)) return program;
    const next = value === undefined && field.startsWith('exponent') ? null : value;
    return { ...program, [bucket]: { ...program[bucket], [field]: next } };
  },
  ...overrides,
});

const programPayField = (field: 'threshold' | 'compensation' | 'exponent_start' | 'exponent', overrides: Pick<GridField, 'label' | 'unit'>): GridField => ({
  key: field,
  longLabel: `Program ${overrides.label.toLowerCase()}`,
  group: 'Pay',
  kind: 'number',
  description: FIELD_DESCRIPTIONS[field],
  perSession: false,
  path: field,
  appliesTo: isLeastPerforming,
  get: (program) => (isLeastPerforming(program) ? (program[field] ?? undefined) : undefined),
  set: (program, value) => (isLeastPerforming(program) ? withKey(program, field, value) : program),
  ...overrides,
});

const nbbSourceField = (field: 'nbb_instrument' | 'nbb_provider', label: string): GridField => ({
  key: field,
  label,
  longLabel: `NBBO ${label.toLowerCase()}`,
  group: 'Index',
  kind: 'text',
  description: FIELD_DESCRIPTIONS.nbb_source,
  perSession: false,
  path: field,
  appliesTo: always,
  get: (program) => program[field],
  set: (program, value) => withKey(program, field, typeof value === 'string' && value.trim() ? value : undefined),
});

/** Open volume needs both values; clearing both removes the block. */
const openVolumeField = (field: 'b2_multiple' | 'compensation_fraction', overrides: Pick<GridField, 'label' | 'longLabel' | 'unit' | 'scale'>): GridField => ({
  key: `ov.${field}`,
  group: 'Open volume',
  kind: 'number',
  description: FIELD_DESCRIPTIONS[`ov.${field}`],
  perSession: false,
  path: `ov.${field}`,
  appliesTo: (_, instrument) => isPerpetual(instrument),
  get: (program) => program.ov?.[field],
  set: (program, value) => {
    const ov = withKey({ ...program.ov }, field, value);
    return withKey(program, 'ov', Object.values(ov).some((item) => item !== undefined) ? ov : undefined);
  },
  ...overrides,
});

const onPerpetual = (_: ProgramInput, instrument: string) => isPerpetual(instrument);

/** Whether a component is applied in production; its values without it only feed backtests. */
const ruleLiveField = (component: RuleComponent, group: FieldGroup): GridField => ({
  key: `rules.live.${component}`,
  label: 'Live',
  longLabel: `${COMPONENT_LABELS[component]} live`,
  group,
  kind: 'boolean',
  description: FIELD_DESCRIPTIONS.live,
  perSession: false,
  path: 'rules.live',
  appliesTo: onPerpetual,
  get: (program) => program.rules?.live.includes(component) === true,
  set: (program, value) => setRuleLive(program, component, value === true),
});

type RuleValue = 'place_rest_ms' | 'move_rest_ms' | 'fill_linger_ms' | 'max_skew_fraction';

const ruleValueField = (field: RuleValue, group: FieldGroup, overrides: Pick<GridField, 'label' | 'unit' | 'scale'>): GridField => ({
  key: `rules.${field}`,
  longLabel: `${group} ${overrides.label.toLowerCase()}`,
  group,
  kind: 'number',
  description: FIELD_DESCRIPTIONS[field],
  perSession: false,
  path: `rules.${field}`,
  appliesTo: onPerpetual,
  get: (program) => program.rules?.[field],
  set: (program, value) => patchRules(program, (rules) => setRuleField(rules, field, typeof value === 'number' ? value : undefined)),
  ...overrides,
});

type RuleBlock = 'volatility' | 'out_of_book';
const BLOCK_DEFAULTS: Record<RuleBlock, Record<string, unknown>> = { volatility: NEW_VOLATILITY, out_of_book: NEW_OUT_OF_BOOK };
const BLOCK_COMPONENTS: Record<RuleBlock, RuleComponent> = { volatility: 'vol', out_of_book: 'oob' };

interface BlockValueConversion {
  read: (stored: unknown) => FieldValue;
  /** Undefined removes the value */
  write: (value: FieldValue) => unknown;
}

const AS_IS: BlockValueConversion = { read: (stored) => stored as FieldValue, write: (value) => value };
/** Rung lists are edited as `10;15;20`, as in the editor. */
const AS_LIST: BlockValueConversion = {
  read: (stored) => (Array.isArray(stored) ? formatNumberList(stored) : undefined),
  write: (value) => (typeof value === 'string' && value.trim() ? parseNumberList(value) : undefined),
};
const AS_TEXT: BlockValueConversion = { read: AS_IS.read, write: (value) => (typeof value === 'string' && value.trim() ? value : undefined) };

/**
 * One value of the volatility or out-of-book block. The first value set starts the block from the editor's defaults;
 * clearing every value removes the block and takes its component out of live.
 */
const ruleBlockField = (
  block: RuleBlock,
  field: string,
  group: FieldGroup,
  overrides: Pick<GridField, 'label' | 'kind' | 'unit' | 'scale' | 'description'>,
  conversion = AS_IS,
): GridField => ({
  key: `rules.${block}.${field}`,
  longLabel: `${group} ${overrides.label.toLowerCase()}`,
  group,
  perSession: false,
  path: `rules.${block}.${field}`,
  appliesTo: onPerpetual,
  get: (program) => {
    const stored = (program.rules?.[block] as Record<string, unknown> | undefined)?.[field];
    return stored === undefined ? undefined : conversion.read(stored);
  },
  set: (program, value) =>
    patchRules(program, (rules) => {
      const next = value === undefined ? undefined : conversion.write(value);
      const current = rules[block] as Record<string, unknown> | undefined;
      if (!current && next === undefined) return rules;
      const values = withKey({ ...(current ?? BLOCK_DEFAULTS[block]) }, field, next);
      if (Object.keys(values).length) return { ...rules, [block]: values } as RulesInput;
      return { ...setRuleField(rules, block, undefined), live: rules.live.filter((component) => component !== BLOCK_COMPONENTS[block]) };
    }),
  ...overrides,
});

const dayField = (field: 'start_date' | 'end_date', label: string): GridField => ({
  key: field,
  label,
  longLabel: label,
  group: 'Schedule',
  kind: 'day',
  description: FIELD_DESCRIPTIONS[field],
  perSession: false,
  path: field,
  appliesTo: always,
  get: (program) => program[field].slice(0, 10),
  set: (program, value) => ({ ...program, [field]: toIsoDate(typeof value === 'string' ? value : '') }),
});

export const GRID_FIELDS: GridField[] = [
  dayField('start_date', 'Start'),
  dayField('end_date', 'End (excl.)'),
  {
    key: 'type',
    label: 'Variant',
    longLabel: 'Variant',
    group: 'Schedule',
    kind: 'type',
    description: FIELD_DESCRIPTIONS.type,
    perSession: false,
    path: 'type',
    appliesTo: always,
    get: (program) => program.type,
    set: (program, value) => (PROGRAM_TYPES.includes(value as ProgramType) ? setProgramType(program, value as ProgramType) : program),
  },
  ...(['b1', 'b2'] as const).flatMap((bucket) => [
    bucketPayField(bucket, 'threshold', { label: 'Threshold', unit: '%' }),
    bucketField(bucket, 'kpi', { label: 'KPI', kind: 'number', unit: 'USD' }),
    bucketField(bucket, 'bps', { label: 'Bps', kind: 'number', unit: 'bps' }),
    bucketField(bucket, 'nbbo', { label: 'NBBO', kind: 'boolean' }),
    bucketPayField(bucket, 'exponent_start', { label: 'Exp. start', unit: '%' }),
    bucketPayField(bucket, 'exponent', { label: 'Exponent' }),
    bucketPayField(bucket, 'compensation', { label: 'Comp.', unit: 'USD' }),
  ]),
  nbbSourceField('nbb_instrument', 'Instrument'),
  nbbSourceField('nbb_provider', 'Provider'),
  programPayField('threshold', { label: 'Threshold', unit: '%' }),
  programPayField('compensation', { label: 'Comp.', unit: 'USD' }),
  programPayField('exponent_start', { label: 'Exp. start', unit: '%' }),
  programPayField('exponent', { label: 'Exponent' }),
  openVolumeField('b2_multiple', { label: 'B2 multiple', longLabel: 'Open volume B2 multiple', unit: '×' }),
  openVolumeField('compensation_fraction', { label: 'Credit', longLabel: 'Open volume credit', unit: '%', scale: 100 }),
  ruleLiveField('rest', 'Rest'),
  ruleValueField('place_rest_ms', 'Rest', { label: 'Place', unit: 'ms' }),
  ruleValueField('move_rest_ms', 'Rest', { label: 'Move', unit: 'ms' }),
  ruleLiveField('linger', 'Linger'),
  ruleValueField('fill_linger_ms', 'Linger', { label: 'Fill', unit: 'ms' }),
  ruleLiveField('skew', 'Skew'),
  ruleValueField('max_skew_fraction', 'Skew', { label: 'Max', unit: '%', scale: 100 }),
  ruleLiveField('vol', 'Volatility'),
  ruleBlockField('volatility', 'rungs_bps', 'Volatility', { label: 'Rungs', kind: 'text', unit: 'bps', description: FIELD_DESCRIPTIONS['volatility.rungs_bps'] }, AS_LIST),
  ruleBlockField('volatility', 'add_bps', 'Volatility', { label: 'Added width', kind: 'text', unit: 'bps', description: FIELD_DESCRIPTIONS['volatility.add_bps'] }, AS_LIST),
  ruleBlockField('volatility', 'cooldown_s', 'Volatility', { label: 'Cooldown', kind: 'number', unit: 's', description: FIELD_DESCRIPTIONS['volatility.cooldown_s'] }),
  ruleBlockField('volatility', 'provider', 'Volatility', { label: 'Provider', kind: 'text', description: FIELD_DESCRIPTIONS['volatility.source'] }, AS_TEXT),
  ruleBlockField('volatility', 'instrument', 'Volatility', { label: 'Symbol', kind: 'text', description: FIELD_DESCRIPTIONS['volatility.source'] }, AS_TEXT),
  ruleLiveField('oob', 'Out of book'),
  ruleBlockField('out_of_book', 'min_gap_s', 'Out of book', { label: 'Min gap', kind: 'number', unit: 's', description: FIELD_DESCRIPTIONS['out_of_book.min_gap_s'] }),
  ruleBlockField('out_of_book', 'full_s', 'Out of book', { label: 'Full cut after', kind: 'number', unit: 's', description: FIELD_DESCRIPTIONS['out_of_book.full_s'] }),
  ruleBlockField('out_of_book', 'max_cut_fraction', 'Out of book', {
    label: 'Max cut',
    kind: 'number',
    unit: '%',
    scale: 100,
    description: FIELD_DESCRIPTIONS['out_of_book.max_cut_fraction'],
  }),
];

export const GRID_FIELDS_BY_KEY = new Map(GRID_FIELDS.map((field) => [field.key, field]));

export const TYPE_OPTIONS = PROGRAM_TYPES.map((type) => ({ value: type, label: PROGRAM_TYPE_LABELS[type] }));
